import { useState } from 'react';
import { Shield, Check, FileText, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Privacy Consent Modal
 * Blocca l'utente finché non accetta la privacy policy al primo accesso.
 * Conforme GDPR - salva timestamp del consenso.
 */
const PrivacyConsentModal = ({ user, famigliaId, onAccept }) => {
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({
    privacy: false,
    dataProcessing: false,
    healthData: false
  });

  const allChecked = checked.privacy && checked.dataProcessing && checked.healthData;

  const handleAccept = async () => {
    if (!allChecked) {
      toast.error('Devi accettare tutti i consensi per continuare');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/privacy/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          famiglia_id: famigliaId,
          user_id: user?.id,
          consents: {
            privacy_policy: checked.privacy,
            data_processing: checked.dataProcessing,
            health_data: checked.healthData
          }
        }),
      });

      if (response.ok) {
        toast.success('Consensi salvati correttamente');
        onAccept();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio dei consensi');
      }
    } catch (error) {
      console.error('Errore salvataggio privacy:', error);
      toast.error('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#4A7C59] to-[#3d6b4a] p-6 rounded-t-3xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Privacy e Consensi</h2>
              <p className="text-white/80 text-sm">Prima di continuare, leggi e accetta</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className="text-gray-600">
            Per utilizzare CuraCheck, è necessario accettare i seguenti consensi in conformità 
            con il <strong>Regolamento GDPR</strong> e la normativa italiana sulla privacy.
          </p>

          {/* Consent 1: Privacy Policy */}
          <label className="flex items-start gap-4 p-4 border border-gray-200 rounded-xl hover:border-[#4A7C59] transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={checked.privacy}
              onChange={(e) => setChecked(prev => ({ ...prev, privacy: e.target.checked }))}
              className="mt-1 w-5 h-5 text-[#4A7C59] rounded border-gray-300 focus:ring-[#4A7C59]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-[#4A7C59]" />
                <span className="font-semibold text-gray-900">Privacy Policy</span>
                <span className="text-red-500">*</span>
              </div>
              <p className="text-sm text-gray-600">
                Ho letto e accetto la{' '}
                <a href="/privacy-policy" target="_blank" className="text-[#4A7C59] underline hover:no-underline">
                  Privacy Policy
                </a>{' '}
                di CuraCheck.
              </p>
            </div>
          </label>

          {/* Consent 2: Data Processing */}
          <label className="flex items-start gap-4 p-4 border border-gray-200 rounded-xl hover:border-[#4A7C59] transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={checked.dataProcessing}
              onChange={(e) => setChecked(prev => ({ ...prev, dataProcessing: e.target.checked }))}
              className="mt-1 w-5 h-5 text-[#4A7C59] rounded border-gray-300 focus:ring-[#4A7C59]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-4 h-4 text-[#4A7C59]" />
                <span className="font-semibold text-gray-900">Trattamento Dati</span>
                <span className="text-red-500">*</span>
              </div>
              <p className="text-sm text-gray-600">
                Acconsento al trattamento dei miei dati personali per la gestione del servizio 
                di assistenza domiciliare, come descritto nella Privacy Policy.
              </p>
            </div>
          </label>

          {/* Consent 3: Health Data */}
          <label className="flex items-start gap-4 p-4 border border-gray-200 rounded-xl hover:border-[#4A7C59] transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={checked.healthData}
              onChange={(e) => setChecked(prev => ({ ...prev, healthData: e.target.checked }))}
              className="mt-1 w-5 h-5 text-[#4A7C59] rounded border-gray-300 focus:ring-[#4A7C59]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-red-500" />
                <span className="font-semibold text-gray-900">Dati Sanitari (Art. 9 GDPR)</span>
                <span className="text-red-500">*</span>
              </div>
              <p className="text-sm text-gray-600">
                Acconsento esplicitamente al trattamento dei dati relativi alla salute 
                (farmaci, terapie, stato di salute dell'assistito) necessari per l'erogazione 
                del servizio di assistenza domiciliare.
              </p>
            </div>
          </label>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              <strong>I tuoi diritti:</strong> Puoi revocare il consenso in qualsiasi momento 
              dalle Impostazioni del tuo account. I tuoi dati sono protetti e trattati in 
              conformità con il GDPR.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100">
          <Button
            onClick={handleAccept}
            disabled={!allChecked || loading}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
              allChecked 
                ? 'bg-[#4A7C59] hover:bg-[#3d6b4a] text-white' 
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Salvataggio...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                Accetto e Continuo
              </span>
            )}
          </Button>
          
          <p className="text-center text-xs text-gray-500 mt-4">
            * Consensi obbligatori per utilizzare il servizio
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentModal;
